import { useState, FormEvent } from 'react';

const ContactFormSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d569guqs3mmh6cm90o3g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-20 lg:py-32 bg-white px-6">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900">
            お問い合わせフォーム
          </h2>
          <p className="text-gray-600 mt-4 text-lg">
            下記フォームに必要事項をご記入の上、送信してください
          </p>
        </div>

        <form 
          onSubmit={handleSubmit} 
          className="bg-gray-50 rounded-3xl p-8 lg:p-12 shadow-lg"
          data-readdy-form
          id="contact-form"
        >
          <div className="space-y-6">
            {/* Name */}
            <div>
              <label htmlFor="name" className="block text-gray-700 font-semibold mb-2">
                お名前 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-[#007bbb] focus:ring-2 focus:ring-[#007bbb]/20 outline-none transition-all text-sm"
                placeholder="山田 太郎"
              />
            </div>

            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-gray-700 font-semibold mb-2">
                メールアドレス <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-[#007bbb] focus:ring-2 focus:ring-[#007bbb]/20 outline-none transition-all text-sm"
                placeholder="example@email.com"
              />
            </div>

            {/* Phone */}
            <div>
              <label htmlFor="phone" className="block text-gray-700 font-semibold mb-2">
                電話番号 <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                required
                className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-[#007bbb] focus:ring-2 focus:ring-[#007bbb]/20 outline-none transition-all text-sm"
                placeholder="090-1234-5678"
              />
            </div>

            {/* Message */}
            <div>
              <label htmlFor="message" className="block text-gray-700 font-semibold mb-2">
                お問い合わせ内容 <span className="text-red-500">*</span>
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={(e) => {
                  if (e.target.value.length <= 500) {
                    setFormData({ ...formData, message: e.target.value });
                  }
                }}
                required
                rows={6}
                maxLength={500}
                className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-[#007bbb] focus:ring-2 focus:ring-[#007bbb]/20 outline-none transition-all resize-none text-sm"
                placeholder="お問い合わせ内容をご記入ください"
              ></textarea>
              <p className="text-sm text-gray-500 mt-2 text-right">
                {formData.message.length} / 500文字
              </p>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-[#007bbb] text-white py-4 rounded-xl font-semibold hover:bg-[#006399] transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed whitespace-nowrap"
            >
              {isSubmitting ? '送信中...' : '送信する'}
            </button>

            {/* Status Messages */}
            {submitStatus === 'success' && (
              <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-xl">
                お問い合わせを受け付けました。ありがとうございます。
              </div>
            )}
            {submitStatus === 'error' && (
              <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-xl">
                送信に失敗しました。もう一度お試しください。
              </div>
            )}
          </div>
        </form>

        {/* Contact Info */}
        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="bg-gray-50 rounded-2xl p-8">
            <div className="w-12 h-12 flex items-center justify-center bg-[#007bbb] rounded-full mb-4">
              <i className="ri-phone-line text-white text-xl"></i>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">お電話でのお問い合わせ</h3>
            <p className="text-gray-600 mb-4">お気軽にお電話ください</p>
            <a href="tel:086-012-3456" className="text-2xl font-bold text-[#007bbb] hover:text-[#006399] transition-colors">
              086-012-3456
            </a>
            <p className="text-sm text-gray-500 mt-2">受付時間：9:00〜18:00（日祝除く）</p>
          </div>

          <div className="bg-gray-50 rounded-2xl p-8">
            <div className="w-12 h-12 flex items-center justify-center bg-[#007bbb] rounded-full mb-4">
              <i className="ri-map-pin-line text-white text-xl"></i>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">所在地</h3>
            <p className="text-gray-600 mb-4">岡山市北区○○町1-2-3</p>
            <p className="text-sm text-gray-500">
              JR岡山駅より徒歩10分<br />
              駐車場完備
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactFormSection;
